import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { PORTIONS, WHOLE_PORTION } from './portions'

type Choice = { id: string; itemCode: string; name: string; servingUnit?: string }

/**
 * The serving fraction and the note for each dish chosen for an item.
 *
 * <p>A meal box is often a quarter of one thing and a third of another rather
 * than four whole dishes, and the kitchen has to know which. The fraction rides
 * with the choice onto the quotation and into the production sheet; it does not
 * change what the package costs.</p>
 *
 * <p>The remark sits beside it for anything the dish itself does not say -
 * "served in a separate bowl", "less spicy for this package" - because the same
 * dish is offered differently by different packages.</p>
 */
export function PortionList({
  chosen,
  portions,
  remarks,
  onChange,
  onRemarksChange,
}: {
  chosen: Choice[]
  portions: Record<string, number>
  remarks: Record<string, string>
  onChange: (portions: Record<string, number>) => void
  onRemarksChange: (remarks: Record<string, string>) => void
}) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Portion per guest — how much of a serving each choice is, and anything worth noting
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {chosen.map((dish) => (
          <Stack
            key={dish.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
          >
            <Typography variant="body2" sx={{ minWidth: 0, flexGrow: 1 }} noWrap>
              {dish.itemCode} — {dish.name}
            </Typography>
            <TextField
              size="small"
              label="Remarks"
              value={remarks[dish.id] ?? ''}
              onChange={(event) =>
                onRemarksChange({ ...remarks, [dish.id]: event.target.value.slice(0, 200) })
              }
              placeholder="Optional"
              sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}
              inputProps={{ 'aria-label': `Remarks for ${dish.name}`, maxLength: 200 }}
            />
            <TextField
              select
              size="small"
              value={portions[dish.id] ?? WHOLE_PORTION}
              onChange={(event) =>
                onChange({ ...portions, [dish.id]: Number(event.target.value) })
              }
              sx={{ width: { xs: '100%', sm: 120 }, flexShrink: 0 }}
              inputProps={{ 'aria-label': `Portion for ${dish.name}` }}
            >
              {PORTIONS.map((portion) => (
                <MenuItem key={portion.label} value={portion.value}>
                  {portion.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}
